/**
 * Explore / Search screen (route: Search, header title: 'Explore').
 *
 * P2-SEARCH REBUILD — X-Explore-style page in CRYB's lavender-glass style:
 *
 *   1. PROMINENT GLASS SEARCH BAR (GlassSurface, variant=bar) — magnifier left,
 *      clear button while typing. Same debounced + out-of-order-guarded machinery
 *      as before.
 *   2. HORIZONTAL CATEGORY TABS (always visible): Top / People / Communities /
 *      Posts / Coins. The Coins tab is SHOW_CRYPTO-gated (info-only CoinDetail —
 *      no value movement). Tabs scroll horizontally as glass pills.
 *   3. TRENDING (idle zero-state) — real data from GET /trending
 *      { posts, communities, users }. Rendered as glass rows grouped under a
 *      "Trending now" header. If the endpoint returns nothing, a small set of
 *      clearly-labelled seed topics is shown (never a blank page, never faked as
 *      live). Recent searches sit above trending.
 *   4. RESULTS GROUPED BY TYPE — the Top tab renders People / Communities / Posts
 *      / Coins as separate labelled sections (avatars for people, square
 *      thumbnails/icons for communities/coins). A single-type tab renders one
 *      flat list. Every row is a GlassCard.
 *
 * Endpoints (all via api(), which prefixes /api/v1, attaches Bearer, and unwraps
 * data.data ?? data):
 *   GET /search?q=&type=       — social buckets { users, posts, communities }
 *   GET /market/tokens/search?q= — coin rows [{ id,name,symbol,marketCapRank,thumb }]
 *   GET /trending              — { posts, communities, users } (idle Trending)
 *
 * Persistence: AsyncStorage[cryb_recent_searches] — last 10 terms.
 *
 * DESIGN BAR: uniform glass + tokens only — no hardcoded rgba/hex/fontSize/radius/
 * spacing/shadow. All surfaces via GlassSurface / GlassCard / GlassPill.
 */
import { useEffect, useState, useRef, useCallback, useMemo, useLayoutEffect } from 'react';
import {
  View,
  Text,
  FlatList,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Keyboard,
  RefreshControl,
  ScrollView,
  StatusBar,
  useWindowDimensions,
} from 'react-native';
import CachedImage from '../components/ui/CachedImage';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import LinearGradient from 'react-native-linear-gradient';
import { useNavigation, useRoute, useFocusEffect } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons, IoniconName } from '../components/ui/Icon';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { api } from '../services/api';
import { communityIcon } from '../lib/icons';
import {
  colors,
  spacing,
  radius,
  fonts,
  typographyScale,
  layout as layoutTokens,
  getLayout,
} from '../constants/theme';
import {
  GlassSurface,
  GlassCard,
  GlassPill,
  GlassPillGroup,
  glassTokens,
} from '../components/glass';
import ParticleBackground from '../components/ui/ParticleBackground';
import ScreenHeading from '../components/ui/ScreenHeading';
import Asset3D from '../components/ui/Asset3D';
import CoinIcon from '../components/markets/CoinIcon';
import { SHOW_CRYPTO } from '../lib/comingSoon';
import type { RootStackParamList } from '../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList>;

type TabKey = 'top' | 'people' | 'communities' | 'posts' | 'coins';
type ResultKind = 'user' | 'community' | 'post' | 'coin';

interface SearchResult {
  type: ResultKind;
  id: string;          // navigation id (username | community name | post id | coin symbol)
  title: string;
  subtitle: string;
  avatar?: string;     // round avatar (users) or square thumbnail (community/coin)
  meta?: string;       // small trailing label (e.g. "12k members", "#3")
  coinName?: string;   // coin display name (for CoinDetail param)
}

// Search tab -> backend social `type` param. The /search API buckets payloads
// under users/posts/communities, so People maps to `users`.
const TAB_TO_TYPE: Record<string, string> = {
  people: 'users',
  posts: 'posts',
  communities: 'communities',
};

interface TabDef { key: TabKey; label: string; icon: IoniconName; cryptoOnly?: boolean; }
// Tab order per client ("arriba de trending deberia ir articulos y arriba
// comunidades"): Communities lead the row, then the aggregate Top ("Trending")
// view, then People / Posts / Coins. Same keys/handlers — order only.
const ALL_TABS: TabDef[] = [
  { key: 'communities', label: 'Communities', icon: 'people-outline' },
  { key: 'top', label: 'Trending', icon: 'flame-outline' },
  { key: 'people', label: 'People', icon: 'person-outline' },
  { key: 'posts', label: 'Posts', icon: 'document-text-outline' },
  { key: 'coins', label: 'Coins', icon: 'logo-bitcoin', cryptoOnly: true },
];

const RECENT_KEY = 'cryb_recent_searches';

// Clearly-labelled seed topics — shown ONLY when /trending returns nothing, so
// the page is never blank and never presents fake live data as real. Tapping a
// seed runs a real search for that term.
//
// Each seed gets a real visual: coins (Bitcoin/Ethereum/Solana) render their
// CoinGecko logo via CoinIcon (the same component the Coins tab uses); concept
// topics (DeFi/NFTs/Web3) get a tasteful brand-tinted gradient tile with a glyph
// so the grid reads like a curated shelf, not flat text (skill §12 material/depth,
// §6 craft). Tapping a seed still just runs a real search for the term.
type SeedMeta =
  | { term: string; kind: 'coin'; symbol: string; image: string; sub: string }
  | { term: string; kind: 'concept'; glyph: IoniconName; gradient: [string, string]; sub: string };

const TRENDING_SEEDS: SeedMeta[] = [
  {
    term: 'Bitcoin',
    kind: 'coin',
    symbol: 'BTC',
    image: 'https://assets.coingecko.com/coins/images/1/large/bitcoin.png',
    sub: 'BTC',
  },
  {
    term: 'Ethereum',
    kind: 'coin',
    symbol: 'ETH',
    image: 'https://assets.coingecko.com/coins/images/279/large/ethereum.png',
    sub: 'ETH',
  },
  {
    term: 'Solana',
    kind: 'coin',
    symbol: 'SOL',
    image: 'https://assets.coingecko.com/coins/images/4128/large/solana.png',
    sub: 'SOL',
  },
  {
    term: 'DeFi',
    kind: 'concept',
    glyph: 'swap-horizontal',
    gradient: ['#6C5CE7', '#A855F7'],
    sub: 'Topic',
  },
  {
    term: 'NFTs',
    kind: 'concept',
    glyph: 'diamond-outline',
    gradient: ['#A855F7', '#EC4899'],
    sub: 'Topic',
  },
  {
    term: 'Web3',
    kind: 'concept',
    glyph: 'globe-outline',
    gradient: ['#3B82F6', '#6C5CE7'],
    sub: 'Topic',
  },
];

// #51: popular topics for the Discover hub. Tapping runs a normal search for the
// term (never a dead end — #54), surfacing communities/posts/people about it.
const TOPIC_SEEDS: { term: string; icon: IoniconName }[] = [
  { term: 'DeFi', icon: 'swap-horizontal' },
  { term: 'NFTs', icon: 'diamond-outline' },
  { term: 'Bitcoin', icon: 'logo-bitcoin' },
  { term: 'Ethereum', icon: 'flash-outline' },
  { term: 'Trading', icon: 'trending-up-outline' },
  { term: 'Airdrops', icon: 'gift-outline' },
  { term: 'Memecoins', icon: 'rocket-outline' },
  { term: 'Web3', icon: 'globe-outline' },
  { term: 'Staking', icon: 'lock-closed-outline' },
  { term: 'Solana', icon: 'sunny-outline' },
];

// =============================================================================
// List coercion (Prisma drift class). Accept array | {data|results|items|users}.
// Always Array.isArray-guard so a reshaped/empty-but-200 envelope degrades to []
// instead of crashing the renderer.
// =============================================================================
function coerceList(node: any): any[] {
  if (Array.isArray(node)) return node;
  if (!node || typeof node !== 'object') return [];
  for (const key of ['results', 'items', 'users', 'data']) {
    if (Array.isArray(node[key])) return node[key];
  }
  return [];
}

// --- Per-entity -> SearchResult mappers --------------------------------------
function userToResult(u: any): SearchResult | null {
  if (!u || typeof u !== 'object') return null;
  // Leaderboard/trending may nest the user under `.user`; search returns it flat.
  const user = u.user && typeof u.user === 'object' ? u.user : u;
  const username = user.username || user.handle;
  // UserProfile navigates by USERNAME (route param), so skip a user with no
  // username — passing a raw id as the username param 404s ("User not found").
  if (!username) return null;
  return {
    type: 'user',
    id: username,
    title: user.displayName || username || 'User',
    subtitle: `@${username}`,
    avatar: user.avatar || user.avatarUrl || undefined,
  };
}

function postToResult(p: any): SearchResult | null {
  if (!p || typeof p !== 'object' || !p.id) return null;
  const author = p.User ?? p.user ?? p.author;
  const body =
    (p.title && String(p.title).trim()) ||
    (p.content ? String(p.content).replace(/\s+/g, ' ').slice(0, 80) : 'Post');
  const comments = p._count?.Comment ?? p.commentCount;
  return {
    type: 'post',
    id: String(p.id),
    title: body,
    subtitle: author?.username ? `@${author.username}` : '',
    avatar: author?.avatar || undefined,
    meta: comments != null ? `${formatCount(Number(comments) || 0)} comments` : undefined,
  };
}

function communityToResult(c: any): SearchResult | null {
  if (!c || typeof c !== 'object') return null;
  const name = c.name || c.slug;
  if (!name && !c.id) return null;
  const members = c.memberCount ?? c._count?.CommunityMember ?? c._count?.ServerMember;
  return {
    type: 'community',
    id: name || c.id,
    title: c.displayName || name || 'Community',
    subtitle: c.description
      ? String(c.description).replace(/\s+/g, ' ').slice(0, 60)
      : (name ? `c/${name}` : ''),
    avatar: c.icon || c.avatar || undefined,
    meta: members != null ? `${formatCount(Number(members) || 0)} members` : undefined,
  };
}

// Coin rows come from /market/tokens/search: { id, name, symbol, marketCapRank,
// thumb }. Info-only surface (CoinDetail) — no value movement.
function coinToResult(c: any): SearchResult | null {
  if (!c || typeof c !== 'object') return null;
  const symbol = c.symbol || c.ticker;
  if (!symbol && !c.id) return null;
  const rank = c.marketCapRank ?? c.market_cap_rank;
  return {
    type: 'coin',
    id: String(symbol || c.id).toUpperCase(),
    title: c.name || String(symbol).toUpperCase(),
    subtitle: symbol ? String(symbol).toUpperCase() : '',
    avatar: c.thumb || c.image || undefined,
    meta: rank != null ? `#${rank}` : undefined,
    coinName: c.name || undefined,
  };
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}k`;
  return String(n);
}

// Flatten a /search or /trending envelope into typed buckets. Each bucket may be
// an array OR a nested {results|items} node.
function flattenBuckets(data: any): Record<ResultKind, SearchResult[]> {
  const out: Record<ResultKind, SearchResult[]> = { user: [], community: [], post: [], coin: [] };
  for (const u of coerceList(data?.users)) { const r = userToResult(u); if (r) out.user.push(r); }
  const commList = data?.communities != null ? data.communities : data?.servers;
  for (const c of coerceList(commList)) { const r = communityToResult(c); if (r) out.community.push(r); }
  for (const p of coerceList(data?.posts)) { const r = postToResult(p); if (r) out.post.push(r); }
  return out;
}

export default function SearchScreen() {
  const navigation = useNavigation<Nav>();
  const route = useRoute<any>();
  const inputRef = useRef<TextInput>(null);
  const insets = useSafeAreaInsets();
  // When opened via the header search icon (navigate('Search', {focusSearch:true}))
  // this IS the dedicated search entry: auto-focus the input so the keyboard is up
  // and the user is searching immediately, not just browsing Discover (#72).
  useFocusEffect(
    useCallback(() => {
      if (route.params?.focusSearch) {
        const t = setTimeout(() => inputRef.current?.focus(), 350);
        return () => clearTimeout(t);
      }
    }, [route.params?.focusSearch])
  );
  const { width } = useWindowDimensions();
  const breakpoint = getLayout(width);
  const containerStyle = breakpoint !== 'phone'
    ? { maxWidth: breakpoint === 'desktop' ? 720 : 620, alignSelf: 'center' as const, width: '100%' as const }
    : undefined;

  // Visible tabs (Coins hidden when crypto surfaces are off).
  const tabs = useMemo(() => ALL_TABS.filter(t => !t.cryptoOnly || SHOW_CRYPTO), []);

  // --- State ---
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<TabKey>('communities');
  const [buckets, setBuckets] = useState<Record<ResultKind, SearchResult[]>>({
    user: [], community: [], post: [], coin: [],
  });
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState(false);

  // Idle (Trending) state.
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [trending, setTrending] = useState<SearchResult[]>([]);
  // "Who to follow" (X right-rail module) — real trending people, kept separate so
  // the Trending list stays a mix while this stays people-only.
  const [whoToFollow, setWhoToFollow] = useState<SearchResult[]>([]);
  // #51: a dedicated "Communities to explore" rail on the idle Discover hub —
  // real communities, not just interleaved into the mixed Trending list.
  const [discoverCommunities, setDiscoverCommunities] = useState<SearchResult[]>([]);
  const [trendingLoading, setTrendingLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Out-of-order guard: every dispatched search bumps this; only the latest
  // sequence commits (slow earlier responses are dropped).
  const seqRef = useRef(0);

  // Hide the opaque native 'Explore' header — this screen owns its own search bar
  // and in-screen title, so the native bar is redundant chrome that breaks the
  // full-bleed translucency (skill §12). Kept single-file via setOptions.
  useLayoutEffect(() => {
    navigation.setOptions({ headerShown: false });
  }, [navigation]);

  useEffect(() => {
    loadRecent();
    fetchTrending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounced search. Empty query -> idle Trending. Re-runs on tab change so the
  // active filter re-queries the current term.
  useEffect(() => {
    if (!query.trim()) {
      setBuckets({ user: [], community: [], post: [], coin: [] });
      setHasSearched(false);
      setSearchError(false);
      seqRef.current++; // cancel any in-flight commit
      return;
    }
    const timer = setTimeout(() => performSearch(), 380);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, activeTab]);

  // --- Recent searches (AsyncStorage-backed) ---
  async function loadRecent() {
    try {
      const stored = await AsyncStorage.getItem(RECENT_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) setRecentSearches(parsed.filter(x => typeof x === 'string'));
      }
    } catch {}
  }

  async function saveRecent(term: string) {
    const updated = [term, ...recentSearches.filter(s => s !== term)].slice(0, 10);
    setRecentSearches(updated);
    try { await AsyncStorage.setItem(RECENT_KEY, JSON.stringify(updated)); } catch {}
  }

  async function clearRecent() {
    setRecentSearches([]);
    try { await AsyncStorage.removeItem(RECENT_KEY); } catch {}
  }

  // --- Trending (idle) --------------------------------------------------------
  // Real data from GET /trending { posts, communities, users }. Interleaved into
  // a single "Trending now" list, capped so it reads like X's trending column.
  const fetchTrending = useCallback(async () => {
    setTrendingLoading(true);
    const res = await api<any>('/trending?limit=25');
    if (res.success && res.data) {
      const b = flattenBuckets(res.data);
      // Communities + people + posts, in that priority, capped at 12.
      const merged = [
        ...b.community.slice(0, 5),
        ...b.user.slice(0, 4),
        ...b.post.slice(0, 4),
      ].slice(0, 12);
      setTrending(merged);
      setWhoToFollow(b.user.slice(0, 3));
      setDiscoverCommunities(b.community.slice(0, 10));
      // Trending can be community-light; backfill the #51 rail from the real
      // directory so "Communities to explore" is never empty.
      if (b.community.length < 4) {
        try {
          const cr = await api<any>('/communities?limit=10');
          const cd: any = cr.data || {};
          const list: any[] = Array.isArray(cd.items) ? cd.items : Array.isArray(cd.communities) ? cd.communities : Array.isArray(cd) ? cd : [];
          if (cr.success && list.length) {
            setDiscoverCommunities(
              list.slice(0, 10).map((c: any): SearchResult => ({
                type: 'community',
                id: c.name,
                title: c.displayName || c.name,
                subtitle: '',
                avatar: c.icon || c.avatar,
                meta: c.memberCount != null ? `${Number(c.memberCount).toLocaleString()} members` : undefined,
              }))
            );
          }
        } catch { /* keep whatever trending gave us */ }
      }
    } else {
      setTrending([]);
      setWhoToFollow([]);
      setDiscoverCommunities([]);
    }
    setTrendingLoading(false);
  }, []);

  async function onRefresh() {
    setRefreshing(true);
    await fetchTrending();
    setRefreshing(false);
  }

  // --- Live search ------------------------------------------------------------
  // Social buckets come from /search; the Coins tab (or the Top tab, additively)
  // pulls from /market/tokens/search. Both are fetched in parallel when needed.
  async function performSearch() {
    if (!query.trim()) return;
    const seq = ++seqRef.current;
    const term = query.trim();
    setSearching(true);
    setHasSearched(true);
    setSearchError(false);

    const wantSocial = activeTab !== 'coins';
    const wantCoins = SHOW_CRYPTO && (activeTab === 'coins' || activeTab === 'top');

    try {
      const socialType = TAB_TO_TYPE[activeTab];
      const [socialRes, coinRes] = await Promise.all([
        wantSocial
          ? api<any>(`/search?q=${encodeURIComponent(term)}${socialType ? `&type=${socialType}` : ''}`)
          : Promise.resolve({ success: true, data: {} } as any),
        wantCoins
          ? api<any>(`/market/tokens/search?q=${encodeURIComponent(term)}`)
          : Promise.resolve({ success: true, data: [] } as any),
      ]);
      if (seq !== seqRef.current) return; // superseded

      const next: Record<ResultKind, SearchResult[]> = { user: [], community: [], post: [], coin: [] };
      if (wantSocial && socialRes.success && socialRes.data) {
        const b = flattenBuckets(socialRes.data);
        next.user = b.user; next.community = b.community; next.post = b.post;
      }
      if (wantCoins && coinRes.success) {
        for (const c of coerceList(coinRes.data)) { const r = coinToResult(c); if (r) next.coin.push(r); }
      }
      setBuckets(next);

      const anyOk = (!wantSocial || socialRes.success) && (!wantCoins || coinRes.success);
      if (!anyOk) setSearchError(true);
    } catch {
      if (seq !== seqRef.current) return;
      setBuckets({ user: [], community: [], post: [], coin: [] });
      setSearchError(true);
    } finally {
      if (seq === seqRef.current) setSearching(false);
    }
    saveRecent(term);
  }

  // --- Navigation (navigate only — never edits destination screens) -----------
  const handleResultPress = useCallback((item: SearchResult) => {
    Keyboard.dismiss();
    switch (item.type) {
      case 'user': navigation.navigate('UserProfile', { username: item.id }); break;
      case 'post': navigation.navigate('Post', { id: item.id }); break;
      case 'community': navigation.navigate('Community', { name: item.id }); break;
      case 'coin':
        navigation.navigate('CoinDetail', { symbol: item.id, name: item.coinName, image: item.avatar });
        break;
    }
  }, [navigation]);

  const listPadBottom = useMemo(
    () => ({ paddingBottom: layoutTokens.bottomNavInset + spacing.xl }),
    []
  );

  // =============================================================================
  // Render helpers
  // =============================================================================
  const ICON_FOR: Record<ResultKind, IoniconName> = {
    user: 'person-circle-outline',
    community: 'people-outline',
    post: 'document-text-outline',
    coin: 'logo-bitcoin',
  };

  function ResultRow({ item }: { item: SearchResult }) {
    const round = item.type === 'user';
    return (
      <GlassCard
        onPress={() => handleResultPress(item)}
        radius={glassTokens.radius.lg}
        padding={spacing.md}
        style={styles.rowCard}
        contentStyle={styles.rowContent}
      >
        {item.avatar ? (
          <CachedImage
            source={{ uri: item.avatar }}
            style={[styles.avatar, round ? styles.avatarRound : styles.avatarSquare]}
          />
        ) : (
          <View style={[styles.iconWrap, round ? styles.avatarRound : styles.avatarSquare]}>
            <Ionicons name={ICON_FOR[item.type]} size={22} color={colors.primary} />
          </View>
        )}
        <View style={styles.rowBody}>
          <Text style={styles.rowTitle} numberOfLines={1}>{item.title || ''}</Text>
          {!!item.subtitle && <Text style={styles.rowSubtitle} numberOfLines={1}>{item.subtitle}</Text>}
        </View>
        {!!item.meta && <Text style={styles.rowMeta} numberOfLines={1}>{item.meta}</Text>}
        <Ionicons name="chevron-forward" size={16} color={colors.faint} style={styles.chevron} />
      </GlassCard>
    );
  }

  // Trend list row (x.com "Trends for you" DNA, recolored to light glass): a clean
  // full-width row — rank/category eyebrow on top, bold term, "Trending" subtitle,
  // and an overflow "…" affordance on the right. Coins keep their CoinGecko logo
  // via CoinIcon; concept topics get a small brand-tinted glyph chip instead of a
  // flat colored tile. Press = built-in GlassSurface scale/dim (skill §1). Tapping
  // runs a real search for the term (unchanged handler).
  function SeedRow({ seed, rank }: { seed: SeedMeta; rank: number }) {
    return (
      <GlassSurface
        variant="card"
        radius={glassTokens.radius.lg}
        // Tapping a trending COIN opens its CoinDetail (was: ran a text search that
        // could dead-end on "No results found", #54). Topics still run a search.
        onPress={() => {
          if (seed.kind === 'coin') {
            navigation.navigate('CoinDetail', { symbol: seed.symbol, name: seed.term, image: seed.image });
          } else {
            setQuery(seed.term);
          }
        }}
        accessibilityLabel={seed.kind === 'coin' ? `Open ${seed.term}` : `Search ${seed.term}`}
        style={styles.trendRow}
        contentStyle={styles.trendRowContent}
      >
        {seed.kind === 'coin' ? (
          <CoinIcon symbol={seed.symbol} image={seed.image} size={40} />
        ) : (
          <LinearGradient
            colors={seed.gradient}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 1 }}
            style={styles.trendGlyphChip}
          >
            <Ionicons name={seed.glyph} size={20} color={colors.white} />
          </LinearGradient>
        )}
        <View style={styles.trendBody}>
          <Text style={styles.trendEyebrow} numberOfLines={1}>
            {rank} · {seed.kind === 'coin' ? 'Coin' : seed.sub}
          </Text>
          <Text style={styles.trendTitle} numberOfLines={1}>{seed.term}</Text>
          <Text style={styles.trendSub} numberOfLines={1}>Trending in CRYB</Text>
        </View>
        <Ionicons
          name="ellipsis-horizontal"
          size={18}
          color={colors.faint}
          style={styles.trendOverflow}
        />
      </GlassSurface>
    );
  }

  // Real trending item as a clean list row (name bold + "Trending · N" subtitle +
  // overflow), NOT a flat colored tile. Coins still get a CoinIcon lead. Uses the
  // same handleResultPress navigation as search results (unchanged).
  function TrendRow({ item, rank }: { item: SearchResult; rank: number }) {
    const category =
      item.type === 'community' ? 'Community'
      : item.type === 'user' ? 'People'
      : item.type === 'coin' ? 'Coin'
      : 'Post';
    return (
      <GlassSurface
        variant="card"
        radius={glassTokens.radius.lg}
        onPress={() => handleResultPress(item)}
        accessibilityLabel={item.title}
        style={styles.trendRow}
        contentStyle={styles.trendRowContent}
      >
        {item.type === 'coin' ? (
          <CoinIcon symbol={item.id} image={item.avatar} size={40} />
        ) : item.avatar ? (
          <CachedImage
            source={{ uri: item.avatar }}
            style={[
              styles.trendLeadImg,
              item.type === 'user' ? styles.avatarRound : styles.avatarSquare,
            ]}
          />
        ) : (
          <View
            style={[
              styles.trendLeadIcon,
              item.type === 'user' ? styles.avatarRound : styles.avatarSquare,
            ]}
          >
            <Ionicons name={ICON_FOR[item.type]} size={20} color={colors.primary} />
          </View>
        )}
        <View style={styles.trendBody}>
          <Text style={styles.trendEyebrow} numberOfLines={1}>{rank} · {category}</Text>
          <Text style={styles.trendTitle} numberOfLines={1}>{item.title || ''}</Text>
          <Text style={styles.trendSub} numberOfLines={1}>
            {item.meta ? `Trending · ${item.meta}` : 'Trending in CRYB'}
          </Text>
        </View>
        <Ionicons
          name="ellipsis-horizontal"
          size={18}
          color={colors.faint}
          style={styles.trendOverflow}
        />
      </GlassSurface>
    );
  }

  // "Who to follow" row — avatar + name + @handle + a "View" pill on the right
  // (X's Follow-button slot; we navigate to the profile rather than mutate follow
  // state here, keeping this screen navigation-only). Tapping anywhere opens the
  // profile via the unchanged handleResultPress.
  function FollowRow({ item }: { item: SearchResult }) {
    return (
      <GlassSurface
        variant="card"
        radius={glassTokens.radius.lg}
        onPress={() => handleResultPress(item)}
        accessibilityLabel={item.title}
        style={styles.trendRow}
        contentStyle={styles.trendRowContent}
      >
        {item.avatar ? (
          <CachedImage source={{ uri: item.avatar }} style={[styles.trendLeadImg, styles.avatarRound]} />
        ) : (
          <View style={[styles.trendLeadIcon, styles.avatarRound]}>
            <Ionicons name="person" size={20} color={colors.primary} />
          </View>
        )}
        <View style={styles.trendBody}>
          <Text style={styles.trendTitle} numberOfLines={1}>{item.title || ''}</Text>
          {!!item.subtitle && (
            <Text style={styles.trendSub} numberOfLines={1}>{item.subtitle}</Text>
          )}
        </View>
        <View style={styles.followPill}>
          <Text style={styles.followPillText}>View</Text>
        </View>
      </GlassSurface>
    );
  }

  function GroupHeader({ title, icon }: { title: string; icon: IoniconName }) {
    return (
      <View style={styles.groupHeader}>
        <Ionicons name={icon} size={15} color={colors.primary} />
        <Text style={styles.groupTitle}>{title}</Text>
      </View>
    );
  }

  // Build the flat renderable list for the current results view. Top tab =
  // grouped sections; single-type tab = one flat group.
  type Renderable =
    | { kind: 'header'; key: string; title: string; icon: IoniconName }
    | { kind: 'row'; key: string; item: SearchResult };

  const resultRenderables = useMemo<Renderable[]>(() => {
    const out: Renderable[] = [];
    const push = (label: string, icon: IoniconName, list: SearchResult[]) => {
      if (!list.length) return;
      out.push({ kind: 'header', key: `h-${label}`, title: label, icon });
      list.forEach((it, i) => out.push({ kind: 'row', key: `${it.type}-${it.id}-${i}`, item: it }));
    };
    if (activeTab === 'top') {
      // Client order: Communities lead, then People / Posts / Coins.
      push('Communities', 'people-outline', buckets.community);
      push('People', 'person-outline', buckets.user);
      push('Posts', 'document-text-outline', buckets.post);
      if (SHOW_CRYPTO) push('Coins', 'logo-bitcoin', buckets.coin);
    } else {
      const map: Record<Exclude<TabKey, 'top'>, [ResultKind, string, IoniconName]> = {
        people: ['user', 'People', 'person-outline'],
        communities: ['community', 'Communities', 'people-outline'],
        posts: ['post', 'Posts', 'document-text-outline'],
        coins: ['coin', 'Coins', 'logo-bitcoin'],
      };
      const [kind, label, icon] = map[activeTab];
      push(label, icon, buckets[kind]);
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buckets, activeTab]);

  const hasAnyResult = resultRenderables.some(r => r.kind === 'row');

  // ---------------------------------------------------------------------------
  // IDLE (Trending) state
  // ---------------------------------------------------------------------------
  function renderIdle() {
    if (trendingLoading && !refreshing) {
      return (
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      );
    }
    const showSeeds = trending.length === 0;
    return (
      <FlatList
        data={[0]}
        keyExtractor={() => 'idle'}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={[styles.listContent, listPadBottom, containerStyle]}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={colors.primary} />
        }
        renderItem={() => (
          <View>
            {/* Recent searches */}
            {recentSearches.length > 0 && (
              <View style={styles.section}>
                <View style={styles.recentHeader}>
                  <Text style={styles.sectionTitle}>Recent</Text>
                  <TouchableOpacity
                    onPress={clearRecent}
                    activeOpacity={0.6}
                    accessibilityRole="button"
                    accessibilityLabel="Clear recent searches"
                    hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                  >
                    <Text style={styles.clearText}>Clear</Text>
                  </TouchableOpacity>
                </View>
                <GlassPillGroup>
                  {recentSearches.map((term, i) => (
                    <GlassPill key={`${term}-${i}`} label={term} icon="time-outline" onPress={() => setQuery(term)} />
                  ))}
                </GlassPillGroup>
              </View>
            )}

            {/* #51 — Communities to explore: a horizontal rail of REAL communities
                (raster-safe icons), the primary thing Discover should surface. */}
            {discoverCommunities.length > 0 && (
              <View style={styles.section}>
                <View style={styles.sectionTitleRow}>
                  <Ionicons name="people" size={18} color={colors.primary} />
                  <Text style={styles.trendingTitle}>Communities to explore</Text>
                </View>
                <ScrollView
                  horizontal
                  showsHorizontalScrollIndicator={false}
                  contentContainerStyle={styles.commRail}
                >
                  {discoverCommunities.map((c, i) => {
                    const img = communityIcon(c.id, c.title, c.avatar);
                    return (
                      <TouchableOpacity
                        key={`${c.id}-${i}`}
                        style={styles.commCard}
                        activeOpacity={0.85}
                        onPress={() => handleResultPress(c)}
                        accessibilityRole="button"
                        accessibilityLabel={`Open ${c.title}`}
                      >
                        {img ? (
                          <CachedImage source={{ uri: img }} style={styles.commAvatar} />
                        ) : (
                          <View style={[styles.commAvatar, styles.commAvatarFallback]}>
                            <Text style={styles.commAvatarLetter}>{(c.title || '?').charAt(0).toUpperCase()}</Text>
                          </View>
                        )}
                        <Text style={styles.commName} numberOfLines={1}>{c.title}</Text>
                        {!!c.meta && <Text style={styles.commMeta} numberOfLines={1}>{c.meta}</Text>}
                      </TouchableOpacity>
                    );
                  })}
                </ScrollView>
              </View>
            )}

            {/* #51 — Popular topics: tappable pills that run a real search. */}
            <View style={styles.section}>
              <View style={styles.sectionTitleRow}>
                <Ionicons name="pricetags" size={17} color={colors.primary} />
                <Text style={styles.trendingTitle}>Popular topics</Text>
              </View>
              <GlassPillGroup>
                {TOPIC_SEEDS.map((t) => (
                  <GlassPill key={t.term} label={t.term} icon={t.icon} onPress={() => setQuery(t.term)} />
                ))}
              </GlassPillGroup>
            </View>

            {/* Trending now — a clean pinned card of list rows (x.com "Trends for
                you" DNA), recolored to light glass. */}
            <View style={styles.section}>
              <View style={styles.sectionTitleRow}>
                <Ionicons name="flame" size={18} color={colors.primary} />
                <Text style={styles.trendingTitle}>Trending now</Text>
              </View>

              {showSeeds ? (
                <View style={styles.trendList}>
                  {TRENDING_SEEDS.map((seed, i) => (
                    <SeedRow key={seed.term} seed={seed} rank={i + 1} />
                  ))}
                </View>
              ) : (
                <View style={styles.trendList}>
                  {trending.map((it, i) => (
                    <TrendRow key={`${it.type}-${it.id}-${i}`} item={it} rank={i + 1} />
                  ))}
                </View>
              )}
            </View>

            {/* Who to follow — X's right-rail module, inlined. Uses the SAME
                trending users (b.user), navigating to their profile via the
                unchanged handleResultPress. Only shown when we have real people. */}
            {whoToFollow.length > 0 && (
              <View style={styles.section}>
                <View style={styles.sectionTitleRow}>
                  <Ionicons name="person-add" size={17} color={colors.primary} />
                  <Text style={styles.trendingTitle}>Who to follow</Text>
                </View>
                <View style={styles.trendList}>
                  {whoToFollow.map((it, i) => (
                    <FollowRow key={`${it.id}-${i}`} item={it} />
                  ))}
                </View>
              </View>
            )}
          </View>
        )}
      />
    );
  }

  // ---------------------------------------------------------------------------
  // RESULTS state (grouped by type)
  // ---------------------------------------------------------------------------
  function renderResults() {
    if (searching) {
      return (
        <View style={styles.loadingWrap}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      );
    }
    return (
      <FlatList
        data={resultRenderables}
        keyExtractor={(r) => r.key}
        renderItem={({ item }) =>
          item.kind === 'header'
            ? <GroupHeader title={item.title} icon={item.icon} />
            : <View style={styles.rowWrap}><ResultRow item={item.item} /></View>
        }
        contentContainerStyle={[styles.listContent, listPadBottom, containerStyle]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        ListEmptyComponent={
          hasAnyResult ? null : searchError ? (
            <View style={styles.emptyContainer}>
              <Ionicons name="cloud-offline-outline" size={48} color={colors.faint} />
              <Text style={styles.emptyText}>Couldn't load results</Text>
              <Text style={styles.emptySubtext}>Check your connection and try again.</Text>
              <TouchableOpacity
                style={styles.retryBtn}
                onPress={performSearch}
                activeOpacity={0.8}
                accessibilityRole="button"
                accessibilityLabel="Retry search"
              >
                <Ionicons name="refresh-outline" size={16} color={colors.white} />
                <Text style={styles.retryText}>Retry</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.emptyContainer}>
              <Asset3D name="compass" size={120} style={{ marginBottom: spacing.sm }} />
              <Text style={styles.emptyText}>No results found</Text>
              <Text style={styles.emptySubtext}>Try different keywords or check your spelling.</Text>
            </View>
          )
        }
      />
    );
  }

  return (
    <View style={styles.screen}>
      {/* Light lavender bg → dark status-bar glyphs. Content scrolls under the
          translucent header, so keep the glyphs dark for contrast (skill §12). */}
      <StatusBar barStyle="dark-content" />

      {/* Full-bleed aurora — fills absoluteFill under the status bar (pointerEvents
          none). Matches Communities/Home/VoiceRooms (skill §7 spatial consistency). */}
      <ParticleBackground theme="light" count={5} />

      {/* Translucent large-title header (ONE glass slab, not three floating chips):
          title + search + category tabs read as a single iOS-style bar. Full-width
          (radius 0) with the notch cleared via insets.top; the FlatLists below
          scroll under it for the full-bleed translucent-material read (skill §12). */}
      <GlassSurface
        variant="bar"
        radius={0}
        surfaceColor="transparent"
        style={[styles.header, styles.headerTransparent]}
        contentStyle={[styles.headerContent, containerStyle, { paddingTop: insets.top + spacing.sm }]}
      >
        {/* Blend into the background gradient (no white bar) — a bg→transparent
            scrim behind the title/search, seamless with the ParticleBackground. */}
        <LinearGradient
          colors={[colors.bg, colors.bg + 'E6', colors.bg + '00']}
          locations={[0, 0.6, 1]}
          style={StyleSheet.absoluteFill}
          pointerEvents="none"
        />
        {/* Row 1 — branded heading (gradient accent) instead of the flat black
            title the client flagged. When pushed as a dedicated Search screen
            (not the Discover tab) show a back chevron — the native header is
            hidden, so without this you're stuck with no way back (#75). */}
        {navigation.canGoBack() && (
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.searchBackBtn}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            accessibilityRole="button"
            accessibilityLabel="Back"
          >
            <Ionicons name="chevron-back" size={26} color={colors.ink} />
          </TouchableOpacity>
        )}
        <ScreenHeading title="Discover" style={styles.headingWrap} />

        {/* Row 2 — prominent glass search field (magnifier + clear affordance). */}
        <GlassSurface
          variant="bar"
          radius={glassTokens.radius.pill}
          style={styles.searchBar}
          contentStyle={styles.searchBarContent}
        >
          <Ionicons name="search" size={20} color={colors.muted} />
          <TextInput
            ref={inputRef}
            style={styles.searchInput}
            placeholder="Search people, communities, coins…"
            placeholderTextColor={colors.muted}
            value={query}
            onChangeText={setQuery}
            returnKeyType="search"
            onSubmitEditing={performSearch}
            autoCapitalize="none"
            autoCorrect={false}
          />
          {query.length > 0 && (
            <TouchableOpacity
              onPress={() => setQuery('')}
              activeOpacity={0.6}
              accessibilityRole="button"
              accessibilityLabel="Clear search text"
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Ionicons name="close-circle" size={19} color={colors.faint} />
            </TouchableOpacity>
          )}
        </GlassSurface>

        {/* Row 3 — horizontal category tabs (glass pills). Right scroll-edge fade
            reads as scrollable, not clipped (skill §12). */}
        <View style={styles.tabRow}>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.tabScrollContent}
          >
            <GlassPillGroup>
              {tabs.map(tab => (
                <GlassPill
                  key={tab.key}
                  label={tab.label}
                  icon={tab.icon}
                  active={activeTab === tab.key}
                  onPress={() => setActiveTab(tab.key)}
                  accessibilityLabel={`Filter by ${tab.label}`}
                />
              ))}
            </GlassPillGroup>
          </ScrollView>
          {/* #52: removed the right-edge fade — over the glass header it read as an
              ugly dark band. The chips just scroll cleanly to the edge now. */}
        </View>
      </GlassSurface>

      {/* (3)/(4) Content — scrolls under the translucent header above. */}
      {!hasSearched ? renderIdle() : renderResults()}
      {/* Compose FAB removed from Discover per feedback — composing lives on Home,
          so Discover stays a clean browse/search surface (no floating button). */}
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  // --- translucent large-title header (single glass slab) ---
  header: {
    // In normal flow at the top of the column so the lists sit under it and
    // scroll beneath its translucency (skill §12). Full-width bar (radius 0).
    zIndex: 2,
  },
  headerTransparent: {
    borderWidth: 0,
    borderBottomWidth: 0,
    shadowOpacity: 0,
    elevation: 0,
    backgroundColor: 'transparent',
  },
  headerContent: {
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.sm,
  },
  // --- in-screen large title (replaces native header) ---
  screenTitle: {
    ...typographyScale.h1,
    color: colors.ink,
    ...fonts.extrabold,
    letterSpacing: -0.4,
  },
  headingWrap: { marginBottom: spacing.md },
  searchBackBtn: { alignSelf: 'flex-start', marginBottom: spacing.xs, marginLeft: -spacing.xs },
  // --- tab row (scroll-edge fade) ---
  tabRow: {
    position: 'relative',
    // Bleed the scroll to the header edges so pills align with the title.
    marginHorizontal: -spacing.lg,
  },
  tabScrollContent: {
    paddingHorizontal: spacing.lg,
    gap: spacing.sm,
  },
  tabEdgeFade: {
    position: 'absolute',
    right: 0,
    top: 0,
    bottom: 0,
    width: 24,
  },
  // --- search bar ---
  searchBar: {
    width: '100%',
  },
  searchBarContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.lg,
    height: 54,
  },
  searchInput: {
    flex: 1,
    ...typographyScale.body,
    color: colors.ink,
  },
  // --- lists ---
  listContent: {
    paddingTop: spacing.xs,
  },
  section: {
    marginTop: spacing.xl,
  },
  sectionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.md,
  },
  sectionTitle: {
    ...typographyScale.h3,
    color: colors.ink,
    ...fonts.bold,
    letterSpacing: -0.2,
  },
  trendingTitle: {
    ...typographyScale.h2,
    color: colors.ink,
    ...fonts.bold,
    letterSpacing: -0.2,
  },
  // #51 — Communities-to-explore rail.
  commRail: { gap: spacing.md, paddingHorizontal: spacing.lg, paddingBottom: spacing.xs },
  commCard: { width: 100, alignItems: 'center' },
  commAvatar: { width: 60, height: 60, borderRadius: 30, marginBottom: spacing.sm, backgroundColor: colors.primary + '10' },
  commAvatarFallback: { alignItems: 'center', justifyContent: 'center' },
  commAvatarLetter: { ...fonts.extrabold, fontSize: 22, color: colors.primary },
  commName: { ...fonts.semibold, fontSize: 13, color: colors.ink, textAlign: 'center' },
  commMeta: { ...fonts.regular, fontSize: 11, color: colors.muted, textAlign: 'center', marginTop: 2 },
  recentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.sm,
  },
  clearText: {
    ...typographyScale.caption,
    color: colors.primary,
    ...fonts.semibold,
  },
  // --- result / trending row ---
  rowWrap: {
    paddingHorizontal: spacing.lg,
    marginBottom: spacing.sm,
  },
  rowCard: {
    width: '100%',
  },
  rowContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
  },
  avatar: {
    width: 44,
    height: 44,
    backgroundColor: colors.faint,
  },
  avatarRound: {
    borderRadius: radius.full,
  },
  avatarSquare: {
    borderRadius: radius.md,
  },
  iconWrap: {
    width: 44,
    height: 44,
    backgroundColor: glassTokens.surface.pill,
    justifyContent: 'center',
    alignItems: 'center',
  },
  rowBody: {
    flex: 1,
  },
  rowTitle: {
    ...typographyScale.body,
    color: colors.ink,
    ...fonts.semibold,
  },
  rowSubtitle: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 1,
    ...fonts.regular,
  },
  rowMeta: {
    ...typographyScale.caption,
    color: colors.ink2,
    ...fonts.medium,
  },
  chevron: {
    marginLeft: spacing.xs,
  },
  // --- grouped result header ---
  groupHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.lg,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
  },
  groupTitle: {
    ...typographyScale.caption,
    color: colors.ink2,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    ...fonts.bold,
  },
  // --- clean trend list rows (x.com "Trends for you", light glass) ---
  trendList: {
    paddingHorizontal: spacing.lg,
    gap: spacing.sm,
  },
  trendRow: {
    width: '100%',
  },
  trendRowContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  // Small brand-tinted glyph chip for concept topics (DeFi/NFTs/Web3) — sized to
  // the 40px CoinIcon so coin + concept rows align.
  trendGlyphChip: {
    width: 40,
    height: 40,
    borderRadius: radius.full,
    justifyContent: 'center',
    alignItems: 'center',
  },
  trendLeadImg: {
    width: 40,
    height: 40,
    backgroundColor: colors.faint,
  },
  trendLeadIcon: {
    width: 40,
    height: 40,
    backgroundColor: glassTokens.surface.pill,
    justifyContent: 'center',
    alignItems: 'center',
  },
  trendBody: {
    flex: 1,
  },
  trendEyebrow: {
    ...typographyScale.caption,
    color: colors.muted,
    ...fonts.medium,
  },
  trendTitle: {
    ...typographyScale.body,
    color: colors.ink,
    ...fonts.bold,
    marginTop: 1,
  },
  trendSub: {
    ...typographyScale.caption,
    color: colors.muted,
    marginTop: 1,
    ...fonts.regular,
  },
  trendOverflow: {
    marginLeft: spacing.xs,
  },
  // Who-to-follow "View" pill (X's Follow-button slot).
  followPill: {
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs + 1,
    borderRadius: radius.full,
    backgroundColor: colors.primary,
  },
  followPillText: {
    ...typographyScale.caption,
    color: colors.white,
    ...fonts.bold,
  },
  // --- states ---
  loadingWrap: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  emptyContainer: {
    alignItems: 'center',
    paddingTop: 72,
    paddingHorizontal: spacing.xxxl,
  },
  emptyText: {
    ...typographyScale.h3,
    color: colors.ink,
    marginTop: spacing.lg,
    ...fonts.semibold,
  },
  emptySubtext: {
    ...typographyScale.body,
    color: colors.muted,
    textAlign: 'center',
    marginTop: spacing.sm,
    ...fonts.regular,
  },
  retryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm + 2,
    borderRadius: radius.full,
    marginTop: spacing.lg,
    gap: spacing.xs,
    minHeight: 44,
  },
  retryText: {
    ...typographyScale.body,
    color: colors.white,
    ...fonts.semibold,
  },
  // --- floating compose (x.com FAB) ---
  fab: {
    position: 'absolute',
    right: spacing.lg,
    width: 58,
    height: 58,
    borderRadius: radius.full,
    shadowColor: colors.primary,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.4,
    shadowRadius: 16,
    elevation: 8,
    zIndex: 3,
  },
  fabInner: {
    flex: 1,
    borderRadius: radius.full,
    justifyContent: 'center',
    alignItems: 'center',
  },
});
